import { FC, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Dropdown, ToggleInput, useDropdownClose, useModal } from 'app/components'
import {
  CategoriaNotificacaoLabels,
  IconPaths,
  Permission,
  QUERIES,
  initialQueryState,
  stringifyRequestQuery,
} from '_metronic/helpers'
import { getNotificacoes } from 'app/modules/apps/notificacoes/core/_requests'
import { NotificacaoList } from 'app/modules/apps/notificacoes/core/_models'
import { useNotificacoes } from 'app/modules/apps/notificacoes/core/NotificacoesProvider'
import { destinoNotificacao } from 'app/modules/apps/notificacoes/core/destinoNotificacao'
import { NotificacaoIcone } from 'app/modules/apps/notificacoes/components/NotificacaoIcone'
import { NotificacaoEnviarModal } from 'app/modules/apps/notificacoes/components/modal/NotificacaoEnviarModal'
import { useAuth } from 'app/modules/auth'
import styles from './HeaderNotificationsMenu.module.scss'

type Tab = 'naoLidas' | 'todas'

const todasQuery = stringifyRequestQuery(initialQueryState)

const EnviarNotificacaoButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const closeDropdown = useDropdownClose()

  return (
    <button
      type='button'
      className='btn btn-link p-0 fs-8 fw-normal'
      onClick={() => {
        closeDropdown()
        onClick()
      }}
    >
      Enviar notificação
    </button>
  )
}

/** Abre pelo despachante; sem referência, só marca como lida (comportamento de sempre do sino). */
const LinhaNotificacao: FC<{ notificacao: NotificacaoList }> = ({ notificacao }) => {
  const { abrirNotificacao } = useNotificacoes()
  const closeDropdown = useDropdownClose()

  const abrir = () => {
    if (destinoNotificacao(notificacao).tipo === 'notaVersao') closeDropdown()
    abrirNotificacao(notificacao, { semReferencia: 'apenasMarcar' })
  }

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={abrir}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          abrir()
        }
      }}
      className={clsx('d-flex gap-3 px-3 py-3 bg-hover-light-primary', styles.row, {
        [styles.rowUnread]: !notificacao.lida,
        'bg-light-primary': !notificacao.lida,
      })}
    >
      <NotificacaoIcone categoria={notificacao.categoria} />
      <div className='flex-grow-1 min-w-0'>
        <div className={clsx('fs-7', notificacao.lida ? 'text-gray-700' : 'text-gray-900')}>
          {notificacao.titulo}
        </div>
        {notificacao.categoria && (
          <div className='fs-9 mt-1 text-gray-700'>
            {CategoriaNotificacaoLabels[notificacao.categoria]}
          </div>
        )}
      </div>
      {!notificacao.lida && <div className={clsx('mt-2', styles.dot)} />}
    </div>
  )
}

const HeaderNotificationsMenu: FC = () => {
  const [tab, setTab] = useState<Tab>('naoLidas')
  const {
    notificacoes: notificacoesNaoLidas,
    naoLidas: totalNaoLidas,
    isLoading: isLoadingNaoLidas,
    marcarTodasComoLidas,
    pushEnabled,
    isTogglingPush,
    togglePush,
  } = useNotificacoes()
  const { currentUser } = useAuth()
  const { showModal } = useModal()
  const queryClient = useQueryClient()

  const isSuperAdmin = currentUser?.grupo?.nome === Permission.SuperAdmin

  const abrirEnvioNotificacao = () => {
    showModal({
      title: 'Enviar notificação',
      content: <NotificacaoEnviarModal />,
      onClose: () => {
        queryClient.invalidateQueries([QUERIES.NOTIFICACOES_LIST])
        queryClient.invalidateQueries([QUERIES.NOTIFICACOES_NAO_LIDAS_COUNT])
      },
    })
  }

  const { data: todasResponse, isFetching: isLoadingTodas } = useQuery(
    [QUERIES.NOTIFICACOES_LIST, todasQuery],
    () => getNotificacoes(todasQuery),
    { enabled: tab === 'todas', cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false }
  )

  const lista: NotificacaoList[] =
    tab === 'naoLidas' ? notificacoesNaoLidas : todasResponse?.data || []
  const isLoading = tab === 'naoLidas' ? isLoadingNaoLidas : isLoadingTodas

  return (
    <div className='position-relative'>
      <Dropdown
        icon={IconPaths.Bell}
        variant='transparent'
        size={360}
        menuClassName={clsx(styles.panel, 'p-0 border rounded overflow-hidden')}
      >
        <div className='d-flex align-items-center justify-content-between px-3 py-3 border-bottom'>
          <span className='fs-6 fw-bold text-gray-900'>Notificações</span>
          <div className='d-flex align-items-center gap-2'>
            {isSuperAdmin && <EnviarNotificacaoButton onClick={abrirEnvioNotificacao} />}
            {totalNaoLidas > 0 && (
              <button
                type='button'
                className='btn btn-link p-0 fs-8 fw-normal'
                onClick={marcarTodasComoLidas}
              >
                Marcar todas como lidas
              </button>
            )}
          </div>
        </div>

        <div className='d-flex gap-4 px-3 border-bottom'>
          <button
            type='button'
            className={clsx('fs-7', styles.tab, {
              [styles.tabActive]: tab === 'naoLidas',
              'text-gray-900': tab === 'naoLidas',
              'text-gray-700': tab !== 'naoLidas',
            })}
            onClick={() => setTab('naoLidas')}
          >
            Não lidas ({totalNaoLidas})
          </button>
          <button
            type='button'
            className={clsx('fs-7', styles.tab, {
              [styles.tabActive]: tab === 'todas',
              'text-gray-900': tab === 'todas',
              'text-gray-700': tab !== 'todas',
            })}
            onClick={() => setTab('todas')}
          >
            Todas
          </button>
        </div>

        <div>
          {isLoading && <div className='text-center fs-8 py-6 text-gray-700'>Carregando...</div>}

          {!isLoading && lista.length === 0 && (
            <div className='text-center fs-8 py-6 text-gray-700'>Nenhuma notificação por aqui.</div>
          )}

          {!isLoading &&
            lista.map((notificacao) => (
              <LinhaNotificacao key={notificacao.id} notificacao={notificacao} />
            ))}
        </div>

        <div className='d-flex align-items-center justify-content-between px-3 py-3 border-top'>
          <Link to='/apps/notificacoes' className='fs-8'>
            Ver todas as notificações
          </Link>

          <label className='d-flex align-items-center gap-2 m-0 text-nowrap cursor-pointer text-gray-700'>
            <span className='fs-9'>
              {pushEnabled ? 'Desativar notificações' : 'Ativar notificações'}
            </span>
            <ToggleInput
              checked={pushEnabled}
              onChange={() => togglePush()}
              disabled={isTogglingPush}
            />
          </label>
        </div>
      </Dropdown>

      {totalNaoLidas > 0 && (
        <span className={styles.badge}>{totalNaoLidas > 99 ? '99+' : totalNaoLidas}</span>
      )}
    </div>
  )
}

export { HeaderNotificationsMenu }
